import {Component, computed, input, OnDestroy, OnInit, output, signal} from '@angular/core';
import {IOrderHistoryView, IOrderHistoryViewProduct, IOrderViewPackage, IOrderViewPackslip} from '../orders-models';
import {BaseComponent} from "../../base.component";
import {UserService} from "../../common/user.service";
import {LoggerService} from "../../common/logger.service";
import * as _ from "lodash";
import {OrderHistoryCacheService} from "../order-history/order-history-cache.service";
import {Subscription} from "rxjs";
import {Constants} from "../../constants";

@Component({
    selector: 'order-history-summary',
    templateUrl: './order-history-summary.component.html',
    styleUrls: ['../orders.scss', './order-history-summary.component.scss'],
    standalone: false
})
export class OrderHistorySummaryComponent extends BaseComponent implements OnInit, OnDestroy {
    readonly orderView = input<IOrderHistoryView>();
    readonly canAutoOrder = input<boolean>(false);
    // passed in rather than read off BaseComponent, whose canPayPendingOrders resolves from a promise
    // and so is not something a computed can react to
    readonly canPayPending = input<boolean>(false);
    readonly displayVolume = input<boolean>(false);
    readonly trackingCount = input<number>(0);
    readonly addFullOrder = output<IOrderHistoryViewProduct[]>();
    readonly viewTracking = output<IOrderHistoryView>();
    readonly managePayment = output<IOrderHistoryView>();

    readonly isAddingToCart = signal<boolean>(false);

    readonly canAddToCart = computed(() =>
        !!_.find(this.orderView()?.products, (product: IOrderHistoryViewProduct) => product?.canAddToCart));

    readonly canManagePayment = computed(() =>
        this.canPayPending()
        && (this.orderView()?.status === Constants.ORDER_STATUS_PENDING
            || this.orderView()?.status === Constants.ORDER_STATUS_PROCESSING));

    // the payment was approved but the order has not finished reconciling; the button is disabled during this window
    readonly isPaymentProcessing = computed(() =>
        this.orderView()?.status === Constants.ORDER_STATUS_PROCESSING);

    // a single tracked package links straight out, so pull its number and link off the packslips
    readonly trackingNumber = computed(() => this.firstTrackedPackage('trackingNumber')?.trackingNumber);
    readonly trackingLink = computed(() => this.firstTrackedPackage('trackingLink')?.trackingLink);

    subscription: Subscription = new Subscription();

    constructor(
        private userService: UserService,
        private loggerService: LoggerService,
        private orderHistoryCacheService: OrderHistoryCacheService
    ) {
        super(userService, loggerService);
    }

    ngOnInit() {
        // any single item is being added to cart for this order OR if the full order is being added to cart
        this.subscription.add(this.orderHistoryCacheService.productsAddingSubject.subscribe((productsAddingMap) => {
            this.isAddingToCart.set(productsAddingMap?.get(this.orderView().id)?.length > 0);
        }));
        this.subscription.add(this.orderHistoryCacheService.orderAddingSubject.subscribe((orderId) => {
            this.isAddingToCart.set(orderId === this.orderView().id);
        }));
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    firstTrackedPackage(property: string): IOrderViewPackage {
        if (this.trackingCount() !== 1) {
            return undefined;
        }
        const packslip: IOrderViewPackslip = _.find(this.orderView()?.packslips, (packslip: IOrderViewPackslip) =>
            !!_.find(packslip?.packages, property)
        );
        return _.find(packslip?.packages, property);
    }

    openOrderTrackingModal(orderView: IOrderHistoryView) {
        this.viewTracking.emit(orderView);
    }

    openManagePaymentModal() {
        this.managePayment.emit(this.orderView());
    }
}
